import { useEffect, useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { TicketEditor } from "./TicketEditor";
import { PriorityBadge, StatusBadge } from "./ticketBadges";
import {
  obtenerTicket,
  agregarComentario,
  confirmarResolucion,
  type Ticket,
  type TicketComment,
  type TicketStatus,
} from "../../services/ticketsService";

interface TicketDetailDialogProps {
  ticketId: string | null;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}

const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };

function isEmptyDoc(doc: any): boolean {
  if (!doc?.content) return true;
  return doc.content.every((node: any) => node.type === "paragraph" && !node.content?.length);
}

function fecha(iso: string) {
  return format(new Date(iso), "d MMM yyyy, HH:mm", { locale: es });
}

export function TicketDetailDialog({ ticketId, onOpenChange, onChanged }: TicketDetailDialogProps) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState<unknown>(EMPTY_DOC);
  const [replyKey, setReplyKey] = useState(0);
  const [sending, setSending] = useState(false);
  const [showReabrirBox, setShowReabrirBox] = useState(false);
  const [reabrirComentario, setReabrirComentario] = useState<unknown>(EMPTY_DOC);
  const [reabrirKey, setReabrirKey] = useState(0);
  const [confirmando, setConfirmando] = useState(false);

  const cargar = async (id: string) => {
    setLoading(true);
    try {
      const { ticket, comments } = await obtenerTicket(id);
      setTicket(ticket);
      setComments(comments);
    } catch (err: any) {
      toast.error("No se pudo cargar el ticket: " + (err.message || ""));
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ticketId) {
      setReply(EMPTY_DOC);
      cargar(ticketId);
    }
  }, [ticketId]);

  const handleReply = async () => {
    if (!ticketId || isEmptyDoc(reply)) return;
    setSending(true);
    try {
      await agregarComentario(ticketId, reply);
      setReply(EMPTY_DOC);
      setReplyKey((k) => k + 1);
      await cargar(ticketId);
      onChanged();
    } catch (err: any) {
      toast.error("No se pudo enviar la respuesta: " + (err.message || ""));
    } finally {
      setSending(false);
    }
  };

  const handleCerrar = async () => {
    if (!ticketId) return;
    setConfirmando(true);
    try {
      await confirmarResolucion(ticketId, "cerrar");
      toast.success("¡Gracias por confirmar! Cerramos el ticket.");
      await cargar(ticketId);
      onChanged();
    } catch (err: any) {
      toast.error("No se pudo cerrar el ticket: " + (err.message || ""));
    } finally {
      setConfirmando(false);
    }
  };

  const handleReabrir = async () => {
    if (!ticketId || isEmptyDoc(reabrirComentario)) return;
    setConfirmando(true);
    try {
      await confirmarResolucion(ticketId, "reabrir", reabrirComentario);
      setReabrirComentario(EMPTY_DOC);
      setReabrirKey((k) => k + 1);
      setShowReabrirBox(false);
      toast.success("Reabrimos el ticket, seguimos con esto.");
      await cargar(ticketId);
      onChanged();
    } catch (err: any) {
      toast.error("No se pudo reabrir el ticket: " + (err.message || ""));
    } finally {
      setConfirmando(false);
    }
  };

  const currentUserEmail = typeof window !== "undefined" ? localStorage.getItem("userEmail") : null;
  const isReporter = !!ticket && !!currentUserEmail && ticket.reporter_email === currentUserEmail;
  const puedeConfirmarResolucion = isReporter && ticket?.status === "resuelto";

  return (
    <Dialog open={!!ticketId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle className="pr-6">{ticket?.title ?? "Cargando ticket..."}</DialogTitle>
          <DialogDescription className="sr-only">
            Detalle del ticket, su historial de respuestas y la opción para responder.
          </DialogDescription>
        </DialogHeader>

        {loading || !ticket ? (
          <div className="text-muted-foreground animate-pulse py-8 text-center">Cargando...</div>
        ) : (
          <>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>{ticket.reporter_name || ticket.reporter_email}</span>
              <span>·</span>
              <span>{fecha(ticket.created_at)}</span>
              <PriorityBadge priority={ticket.priority} />
              <StatusBadge status={ticket.status} />
              {ticket.module && (
                <Badge variant="outline">
                  {ticket.module}
                  {ticket.section && <> · {ticket.section}</>}
                </Badge>
              )}
              {ticket.assigned_to_name || ticket.assigned_to_email ? (
                <Badge variant="outline">
                  Asignado a: {ticket.assigned_to_name || ticket.assigned_to_email}
                </Badge>
              ) : null}
            </div>

            {puedeConfirmarResolucion && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 space-y-2">
                <p className="text-sm font-medium">Marcamos este ticket como resuelto — ¿quedó bien?</p>
                {!showReabrirBox ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCerrar} disabled={confirmando}>
                      Sí, ciérralo
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowReabrirBox(true)}
                      disabled={confirmando}
                    >
                      No, reabrir
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <TicketEditor
                      key={reabrirKey}
                      content={reabrirComentario}
                      onChange={setReabrirComentario}
                      placeholder="Cuéntanos qué falta o qué pasó..."
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setShowReabrirBox(false)}>
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleReabrir}
                        disabled={confirmando || isEmptyDoc(reabrirComentario)}
                      >
                        {confirmando ? "Reabriendo..." : "Confirmar reapertura"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Separator className="my-2" />

            <TicketEditor content={ticket.content} editable={false} />

            {comments.length > 0 && (
              <>
                <Separator className="my-2" />
                <div className="space-y-3">
                  {comments.map((c) =>
                    c.is_status_change ? (
                      <div key={c.id} className="text-xs text-muted-foreground italic">
                        {c.author_name || c.author_email} cambió el estado de{" "}
                        <StatusBadge status={c.old_status as TicketStatus} /> a{" "}
                        <StatusBadge status={c.new_status as TicketStatus} /> · {fecha(c.created_at)}
                      </div>
                    ) : (
                      <div key={c.id} className="border rounded-md p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                          <span className="font-medium text-foreground">
                            {c.author_name || c.author_email}
                          </span>{" "}
                          · {fecha(c.created_at)}
                        </div>
                        <TicketEditor content={c.content} editable={false} />
                      </div>
                    ),
                  )}
                </div>
              </>
            )}
          </div>

          <div className="shrink-0 border-t bg-background px-6 py-3 space-y-2">
            <TicketEditor
              key={replyKey}
              content={reply}
              onChange={setReply}
              placeholder="Escribe una respuesta..."
            />
            <div className="flex justify-end">
              <Button onClick={handleReply} disabled={sending}>
                {sending ? "Enviando..." : "Responder"}
              </Button>
            </div>
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
